/* eslint-disable jsx-a11y/anchor-is-valid */
import {FC, useState, useEffect, useMemo, useRef, useCallback} from 'react'
import {useFormik} from 'formik'
import clsx from 'clsx'
import * as Yup from 'yup'
import {useIntl} from 'react-intl'
import XHRUpload from '@uppy/xhr-upload'
import Uppy from '@uppy/core'
import {Dashboard as DashboardComponent} from '@uppy/react'

import {
  PROFESSIONAL_RETOUCH_JP_PRICE,
  ORDER_IMAGE_SUPPORTED_TYPE,
  ORDER_NAME_FORMAT,
  OrderSettingType,
  OrderType,
  PROFESSIONAL_RETOUCH_US_PRICE,
  SETTING_CATEGORY_INDOOR_ID,
} from '../constants'
import {ServiceModel} from '../../../modules/services/models/ServiceModel'
import {UPLOAD_FILE_URL} from '../redux/OrderApi'
import {OrderRequestModel} from '../models/OrderRequestModel'
import {useAppSelector} from '../../../../setup/redux/hooks'
import {createOrder, selectOrderLoading} from '../redux/OrderRedux'
import {selectAccessToken, selectUser} from '../../../modules/auth'
import {
  MAX_INDIVIDUAL_FILE_SIZE,
  MAX_INDIVIDUAL_UPLOAD_ITEMS,
  MAX_ENTERPRISE_UPLOAD_ITEMS,
  MAX_ENTERPRISE_FILE_SIZE,
  PAYMENT_METHOD,
  MAX_SUBSCRIPTION_UPLOAD_ITEMS,
  PHOTO_ENHANCEMENT_SERVICE_ID,
} from '../../../shared/constants'
import {useDispatch} from 'react-redux'
import {PlanType} from '../../setting-reference/constants'
import {BETA_SERVICE_NAMES} from '../../../modules/services/constants'
import Japanese from '@uppy/locales/lib/ja_JP'
import JapaneseString from '../../../../_metronic/i18n/messages/uppy_ja.json'
import PayOrderModal from './modals/payOrderModal'
import ErrorBoundary from '../../../components/ErrorBoundary'
import Tooltip from 'react-tooltip-lite'
import moment from 'moment'
import {CurrencyCode, CurrencySymbol} from '../../pricing/pricing.constant'
import {COUNTRY_CODES} from '../../../modules/profile/constants'
import {formatNumber} from '../../../shared/utils'
import guardDuty from '../img/guardduty.png'
import '@uppy/core/dist/style.css'
import '@uppy/dashboard/dist/style.css'
import './newOrder.scss'
import {SettingSelectionSection} from './SettingSelectionSection'
import {SettingReferenceModel} from '../../setting-reference/models/SettingReferenceModel'
import {SettingGuide} from './SettingGuide'

interface Props {
  service: ServiceModel
}

const NewOrderComponent: FC<Props> = ({service}) => {
  const intl = useIntl()
  const currentLocale = intl.locale
  const dispatch = useDispatch()
  const isBetaService = service?.name && BETA_SERVICE_NAMES.includes(service.name.toLowerCase())
  const formikRef = useRef<any>(null)
  const [isShowPaymentModal, setIsShowPaymentModal] = useState(false)
  const [currentSettingCategory, setCurrentSettingCategory] = useState<number | null>(null)
  const [imageLUploading, setImageUploading] = useState(false)
  const [settingSummary, setSettingSummary] = useState({
    unitPrice: 0,
    extraPaid: 0,
    aiSupported: true,
  })
  const loading = useAppSelector(selectOrderLoading)
  const [uppy, setUppy] = useState<Uppy | null>(null)
  const accessToken = useAppSelector(selectAccessToken)
  const user = useAppSelector(selectUser)

  useEffect(() => {
    Japanese.strings = JapaneseString
  }, [])

  const currency = useMemo(
    () => (user?.countryCode === COUNTRY_CODES.JP ? CurrencyCode.JPY : CurrencyCode.USD),
    [user?.countryCode]
  )

  const initialValues: OrderRequestModel = useMemo(
    () => ({
      name: moment().format(ORDER_NAME_FORMAT),
      type: OrderType.AI_ONLY,
      serviceId: service.id,
      itemKeys: [],
      settingType: OrderSettingType.PRE_SETTING,
      settingId: 0,
      paymentMethod: PAYMENT_METHOD.CREDIT,
      customSetting: undefined,
      currency: currency,
    }),
    [currency, service.id]
  )
  const orderSchema = useMemo(
    () =>
      Yup.object().shape({
        name: Yup.string()
          .min(1, intl.formatMessage({id: 'Order.name.MIN'}))
          .max(50, intl.formatMessage({id: 'Order.name.MAX'})),
        itemKeys: Yup.array()
          .min(1, intl.formatMessage({id: 'Order.items.MIN'}))
          .max(100, intl.formatMessage({id: 'Order.items.MAX'}))
          .required(intl.formatMessage({id: 'Order.items.REQUIRED'})),
        type: Yup.string().required(intl.formatMessage({id: 'Order.type.REQUIRED'})),
        serviceId: Yup.string().required(intl.formatMessage({id: 'Order.service.REQUIRED'})),
        settingType: Yup.string().required(intl.formatMessage({id: 'Order.settingType.REQUIRED'})),
        settingId: Yup.string().when('settingType', {
          is: (value: OrderSettingType) => value === OrderSettingType.PRE_SETTING,
          then: Yup.string().required(intl.formatMessage({id: 'Order.setting.REQUIRED'})),
        }),
        customSetting: Yup.array()
          .of(
            Yup.object().shape({
              featureId: Yup.number().required(),
              value: Yup.string().required(),
            })
          )
          .notRequired(),
      }),
    [intl]
  )
  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: orderSchema,
    onSubmit: (values, {setStatus, setSubmitting, resetForm}) => {
      if (values.type === OrderType.AI_ONLY) {
        values.currency = currency
        dispatch(createOrder(values))
        formik.resetForm()
      } else {
        setIsShowPaymentModal(true)
      }
      setImageUploading(false)
    },
  })
  formikRef.current = formik

  useEffect(() => {
    setIsShowPaymentModal(false)
    setImageUploading(false)
  }, [service.id])
  // ---------------------Functions-------------------------------------------
  const handlePaymentMethodChanged = useCallback(
    (method: any) => {
      formik.setFieldValue('paymentMethod', method)
    },
    [formik]
  )

  const uppyLocale = useMemo(() => (currentLocale === 'ja' ? Japanese : undefined), [currentLocale])
  const uppyRestrictions = useMemo(
    () => ({
      maxNumberOfFiles:
        user?.planType === PlanType.SUBSCRIPTION_PREMIUM
          ? MAX_ENTERPRISE_UPLOAD_ITEMS
          : user?.planType === PlanType.SUBSCRIPTION_STANDARD ||
            user?.planType === PlanType.SUBSCRIPTION_BASIC
          ? MAX_SUBSCRIPTION_UPLOAD_ITEMS
          : MAX_INDIVIDUAL_UPLOAD_ITEMS,
      allowedFileTypes: ORDER_IMAGE_SUPPORTED_TYPE,
      maxFileSize:
        user?.planType === PlanType.SUBSCRIPTION_PREMIUM
          ? MAX_ENTERPRISE_FILE_SIZE
          : MAX_INDIVIDUAL_FILE_SIZE,
    }),
    [user?.planType]
  )

  useEffect(() => {
    const newUppy = new Uppy({
      locale: uppyLocale,
      restrictions: uppyRestrictions,
      autoProceed: true,
      debug: true,
    })
    newUppy.use(XHRUpload, {
      endpoint: UPLOAD_FILE_URL,
      headers: {authorization: `Bearer ${accessToken}`},
      fieldName: 'file',
      allowedMetaFields: ['serviceId'],
    })
    newUppy.on('upload-success', (file: any, response: any) => {
      if (newUppy) {
        newUppy.setFileMeta(file.id, {
          key: response.body.key,
        })
      }
      const currentFormik = formikRef.current
      if (currentFormik) {
        // Use functional update to avoid losing keys when multiple uploads finish together.
        currentFormik.setFormikState((prevState: any) => ({
          ...prevState,
          values: {
            ...prevState.values,
            itemKeys: [...(prevState.values.itemKeys ?? []), response.body.key],
          },
          errors: Object.fromEntries(
            Object.entries(prevState.errors ?? {}).filter(([key]) => key !== 'itemKeys')
          ),
        }))
      }
    })
    newUppy.on('upload-error', (file: any, error: any, response: any) => {
      newUppy.removeFile(file.id)
      newUppy.info(
        intl.formatMessage({id: 'Order.items.UPLOAD_FAILED'}, {fileName: file.name}),
        'error',
        5000
      )
    })
    newUppy.on('complete', () => {
      setImageUploading(false)
    })
    newUppy.on('file-added', async (file) => {
      const rawFile = file.data
      newUppy.setFileMeta(file.id, {
        serviceId: service.id,
      })
      if (rawFile instanceof File) {
        setImageUploading(true)
      } else {
        newUppy.removeFile(file.id)
        newUppy.info('Invalid file type', 'error', 5000)
      }
    })
    newUppy.on('file-removed', (file: any) => {
      const currentFormik = formikRef.current
      if (currentFormik) {
        currentFormik.setFormikState((prevState: any) => ({
          ...prevState,
          values: {
            ...prevState.values,
            itemKeys: (prevState.values.itemKeys ?? []).filter(
              (key: string) => key !== file.meta.key
            ),
          },
        }))
        // Defer validation so it runs against the freshly updated values.
        setTimeout(() => currentFormik.validateField('itemKeys'), 0)
      }
    })
    setUppy(newUppy)

    return () => {
      // First set uppy to null to hide the Dashboard component
      // before calling close() to prevent DOM synchronization issues
      setUppy(null)
      // Use double requestAnimationFrame + delay to ensure React completes
      // its render cycle before Uppy performs internal cleanup
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setTimeout(() => {
            try {
              newUppy.close()
            } catch (e) {
              console.warn('Uppy cleanup error (safe to ignore):', e)
            }
          }, 100)
        })
      })
    }
  }, [accessToken, service.id, uppyLocale, uppyRestrictions])
  const unitPrice = useMemo(() => settingSummary.unitPrice, [settingSummary.unitPrice])
  const extraPaid = useMemo(() => settingSummary.extraPaid, [settingSummary.extraPaid])
  const extraPaidCredit = useMemo(
    () => extraPaid / (user?.userRate?.price ?? 1),
    [extraPaid, user?.userRate?.price]
  )
  const aiSupported = settingSummary.aiSupported
  const professionalRetouchPrice = useMemo(
    () =>
      (currency === CurrencyCode.JPY
        ? PROFESSIONAL_RETOUCH_JP_PRICE
        : PROFESSIONAL_RETOUCH_US_PRICE) / (user?.userRate?.price ?? 1),
    [currency, user?.userRate?.price]
  )
  const serviceNameLabel = useMemo(
    () => intl.formatMessage({id: 'SERVICE.SERVICE.' + service.name}),
    [intl, service.name]
  )
  const orderDetails = useMemo(
    () => [
      {
        itemName: serviceNameLabel,
        unitPrice: unitPrice,
        quantity: formik.values.itemKeys.length,
      },
      {
        itemName: intl.formatMessage({id: 'SERVICE.SERVICE.EXTRA_PAID'}),
        unitPrice: extraPaidCredit,
        quantity: formik.values.itemKeys.length,
        fiatAmount: extraPaid,
      },
      {
        itemName: intl.formatMessage({id: 'SERVICE.SERVICE.HUMAN_RETOUCH'}),
        unitPrice: professionalRetouchPrice,
        quantity: formik.values.itemKeys.length,
        fiatAmount:
          currency === CurrencyCode.JPY
            ? PROFESSIONAL_RETOUCH_JP_PRICE
            : PROFESSIONAL_RETOUCH_US_PRICE,
      },
    ],
    [
      currency,
      extraPaid,
      extraPaidCredit,
      formik.values.itemKeys.length,
      intl,
      professionalRetouchPrice,
      serviceNameLabel,
      unitPrice,
    ]
  )
  return (
    <div className='card w-100'>
      <div className='card-body p-4 p-lg-6'>
        <form
          className='row g-5'
          onSubmit={formik.handleSubmit}
          noValidate
          id='kt_create_order_form'
        >
          {formik.status && (
            <div className='mb-lg-15 alert alert-danger'>
              <div className='alert-text fw-bolder'>{formik.status}</div>
            </div>
          )}
          <div className='col-12 col-lg-4 d-flex flex-column gap-5'>
            <div className='col-md-12 d-flex flex-column gap-3'>
              <label className='form-label mb-3 fw-bolder'>
                {intl.formatMessage({id: 'Order.name.TITLE'})}
              </label>

              <input
                type='text'
                className={clsx(
                  'form-control form-control-lg form-control-solid',
                  {'is-invalid': formik.touched.name && formik.errors.name},
                  {
                    'is-valid': formik.touched.name && !formik.errors.name,
                  }
                )}
                {...formik.getFieldProps('name')}
                placeholder={intl.formatMessage({id: 'Order.name.DESCRIPTION'})}
              />
              {formik.touched.name && formik.errors.name && (
                <div className='fv-plugins-message-container'>
                  <span role='alert'>{formik.errors.name}</span>
                </div>
              )}
            </div>
            <div className='col-md-12 d-flex flex-column gap-3'>
              <label className='form-label mb-3 fw-bolder'>
                {intl.formatMessage({id: 'Order.name.SERVICE'})}
                {isBetaService && (
                  <span className='badge badge-danger ms-2 align-middle'>
                    {intl.formatMessage({id: 'SERVICE.TAG.BETA'})}
                  </span>
                )}
              </label>

              <input
                type='text'
                disabled={true}
                value={serviceNameLabel}
                className='form-control form-control-lg form-control-solid'
              />
            </div>

            <div className='rounded-3 p-3 bg-white'>
              <SettingSelectionSection
                key={service.id}
                service={service}
                onSettingChanged={(
                  newSetting: SettingReferenceModel,
                  customSetting: any = undefined
                ) => {
                  if (!newSetting) {
                    return
                  }
                  formik.setFieldValue('settingId', newSetting.id)
                  formik.setFieldValue('customSetting', customSetting)
                  setCurrentSettingCategory(newSetting.categoryId)
                  const unitPrice = newSetting.basePrice ? newSetting.basePrice : 0
                  if (unitPrice === 0 && formik.values.type === OrderType.PROFESSIONAL) {
                    formik.setFieldValue('type', OrderType.AI_ONLY)
                  }
                  const aiSupported = newSetting.isAiSupport ?? true
                  if (!aiSupported && formik.values.type === OrderType.AI_ONLY) {
                    formik.setFieldValue('type', OrderType.PROFESSIONAL)
                  }
                  setSettingSummary({
                    unitPrice: unitPrice,
                    extraPaid:
                      newSetting.totalAdditionalFee?.find((fee) => fee.currency === currency)
                        ?.price ?? 0,
                    aiSupported: newSetting.isAiSupport ?? true,
                  })
                }}
              />
            </div>
            <div className='d-flex flex-column gap-3'>
              <div className='d-flex align-items-center mb-1'>
                <label className='form-label mb-3 fw-bolder'>
                  {intl.formatMessage({id: 'Order.type'})}
                </label>
                <Tooltip content={intl.formatMessage({id: 'Order.type.DES'})} className='tooltips'>
                  <i className='text-gray-400 fas fa-exclamation-circle ms-1 mb-3 fs-7'></i>
                </Tooltip>
              </div>
              <div className='row g-4'>
                <div className='col-12 col-md-12'>
                  <label className='form-check form-check-sm form-check-custom form-check-solid fs-6 mb-1'>
                    <input
                      className='form-check-input'
                      type='radio'
                      value={OrderType.AI_ONLY}
                      disabled={!aiSupported}
                      name='order-quality'
                      checked={formik.values.type === OrderType.AI_ONLY}
                      onChange={() => {
                        formik.setFieldValue('type', OrderType.AI_ONLY)
                      }}
                    />
                    <span className='form-check-label fw-bold text-gray-900'>
                      {intl.formatMessage({id: 'Order.type.ai'})}

                      <span className='badge badge-light-success fs-8 fw-semibold px-2 py-1 ms-1'>
                        {intl.formatMessage({id: 'Order.setting.PREFIX_SETTING.SUB'})}
                      </span>
                    </span>
                  </label>

                  <div className='text-gray-600 fw-semibold fs-7 ps-7'>
                    <div className='d-flex align-items-center mb-1'>
                      <span className='bullet me-2'></span>
                      <span>{intl.formatMessage({id: 'Order.type.ai.PROCESSING_TIME'})}</span>
                    </div>
                    <div className='d-flex align-items-center'>
                      <span className='bullet me-2'></span>
                      <span>{intl.formatMessage({id: 'Order.type.ai.PAYMENT'})}</span>
                    </div>
                  </div>
                </div>
                <div className='col-12 col-md-12'>
                  <label className='form-check form-check-sm form-check-custom form-check-solid fs-6 mb-1'>
                    <input
                      disabled={unitPrice === 0 || service.id !== PHOTO_ENHANCEMENT_SERVICE_ID}
                      className='form-check-input'
                      type='radio'
                      value={OrderType.PROFESSIONAL}
                      name='order-quality'
                      checked={formik.values.type === OrderType.PROFESSIONAL}
                      onChange={() => {
                        formik.setFieldValue('type', OrderType.PROFESSIONAL)
                      }}
                    />
                    <span className='form-check-label fw-bold text-gray-900'>
                      {intl.formatMessage({id: 'Order.type.professional'})}
                      {service.id === PHOTO_ENHANCEMENT_SERVICE_ID ? (
                        <span className='badge badge-light-danger fs-8 fw-semibold px-2 py-1 ms-1'>
                          +{' '}
                          {currency === CurrencyCode.JPY
                            ? PROFESSIONAL_RETOUCH_JP_PRICE + '円'
                            : CurrencySymbol.USD + PROFESSIONAL_RETOUCH_US_PRICE}{' '}
                          / {intl.formatMessage({id: 'INQUIRY.TABLE.ROW.ITEM'})} {' ('}
                          {formatNumber(professionalRetouchPrice) +
                            ' ' +
                            intl.formatMessage({id: 'SYSTEM.CURRENCY'})}
                          {')'}
                        </span>
                      ) : (
                        <span className='badge badge-light-danger fs-8 fw-semibold px-2 py-1 ms-1'>
                          {intl.formatMessage({id: 'GENERAL.COMING'})}
                        </span>
                      )}
                    </span>
                  </label>

                  <div className='text-gray-600 fw-semibold fs-7 ps-7'>
                    <div className='d-flex align-items-center mb-1'>
                      <span className='bullet me-2'></span>
                      <span>
                        {intl.formatMessage({id: 'Order.type.professional.PROCESSING_TIME'})}
                      </span>
                    </div>
                    <div className='d-flex align-items-center'>
                      <span className='bullet me-2'></span>
                      <span>{intl.formatMessage({id: 'Order.type.professional.PAYMENT'})}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className='col-12 col-lg-8 d-flex flex-column gap-5'>
            <div className='fv-row'>
              <label className='form-label mb-3 w-100 d-flex fw-bolder'>
                {intl.formatMessage({id: 'Order.items.TITLE'})}{' '}
                {formik.values.itemKeys.length > 0 &&
                  `(${formik.values.itemKeys.length} ` +
                    intl.formatMessage({id: 'Order.items.UPLOADED'}) +
                    ')'}
              </label>
              {uppy && (
                <div className='border rounded-3 p-2 bg-white' style={{position: 'relative'}}>
                  <ErrorBoundary
                    fallback={<div className='p-4 text-center text-muted'>{intl.formatMessage({id: 'MESSAGE.COMPONENT.RELOADING'})}</div>}
                  >
                    <DashboardComponent
                      width={'100%'}
                      height={'400px'}
                      uppy={uppy}
                      showRemoveButtonAfterComplete={true}
                      doneButtonHandler={formik.handleSubmit}
                    />
                  </ErrorBoundary>
                  <img
                    src={guardDuty}
                    alt='Malware protection'
                    style={{
                      position: 'absolute',
                      bottom: '-40px',
                      right: '10px',
                      width: '100px',
                      boxShadow: '0 0 10px 0 rgba(0,0,0,0.1)',
                    }}
                  />
                </div>
              )}
              {formik.errors.itemKeys && (
                <div className='fv-plugins-message-container'>
                  <span role='alert'>{formik.errors.itemKeys}</span>
                </div>
              )}
            </div>

            <div className='d-flex align-items-center ps-0 mb-2'>
              <span className='text-danger fs-7' style={{width: '80%'}}>
                *{intl.formatMessage({id: 'Order.items.' + user?.planType + '.rule'})}
              </span>
            </div>
            {currentSettingCategory === SETTING_CATEGORY_INDOOR_ID && (
              <div className='col-12 col-lg-12'>
                <SettingGuide
                  categoryName={currentSettingCategory}
                  serviceId={service.id}
                  className='h-100'
                />
              </div>
            )}
            <div className='d-flex justify-content-end'>
              <button
                type='submit'
                className='btn btn-lg btn-primary'
                disabled={!formik.isValid || loading || imageLUploading}
              >
                {!loading && (
                  <span className='indicator-label'>
                    {intl.formatMessage({id: 'Login.form.CONTINUE_BTN'})}
                  </span>
                )}
                {loading && (
                  <span className='indicator-progress' style={{display: 'block'}}>
                    {intl.formatMessage({id: 'Login.form.WAITING'})}
                    <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                  </span>
                )}
              </button>
            </div>
          </div>
        </form>
        <PayOrderModal
          isShow={isShowPaymentModal}
          onClose={() => setIsShowPaymentModal(false)}
          onPaymentMethodChange={handlePaymentMethodChanged}
          currency={currency}
          orderDetails={orderDetails}
          onOk={() => {
            setIsShowPaymentModal(false)
            dispatch(createOrder(formik.values))
          }}
        />
      </div>
    </div>
  )
}

export {NewOrderComponent}
